import { createContext, ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import { ApolloClient, useQuery } from '@apollo/client';
import { useLocale } from '../../../../../contexts/LocaleProvider';
import {
    GET_RESOURCE_TYPES_QUERY,
    getResourceTypesQueryVariables,
} from '../queries/getResourceTypesQuery';
import { ResourceType } from '../types/ResourceType';
import { transformResourceTypeResponse } from '../tools/transformResourceTypeResponse';
import { GetResourceTypeListingQuery } from '../schemas/generated-types/graphql';
import { useQueryFallbackResponse } from '../../../common/hooks/useQueryFallbackResponse';

type ResourceTypeById = Record<string, ResourceType>;
type ResourceTypeByKey = Record<string, ResourceType>;

interface ResourceTypesContextValue {
    resourceTypes: ResourceType[];
    resourceTypeById: ResourceTypeById;
    resourceTypeByKey: ResourceTypeByKey;
    loading: boolean;
    error: any;
    initialize: () => void;
}

const ResourceTypesContext = createContext<ResourceTypesContextValue | undefined>(undefined);

interface ResourceTypesProviderProps {
    children: ReactNode;
    apolloClient: ApolloClient<any>;
}

const ResourceTypesProvider = ({ children, apolloClient }: ResourceTypesProviderProps) => {
    const { locale } = useLocale();

    const [resourceTypeById, setResourceTypeById] = useState<ResourceTypeById>({});
    const [resourceTypeByKey, setResourceTypeByKey] = useState<ResourceTypeByKey>({});

    const [initialized, setInitialized] = useState(false);

    const query = GET_RESOURCE_TYPES_QUERY;
    const {
        loading: loadingQuery,
        error: queryError,
        data: queryData,
    } = useQuery<GetResourceTypeListingQuery>(query, {
        variables: getResourceTypesQueryVariables(locale),
        client: apolloClient,
        errorPolicy: 'all', // Allow partial data instead of full rejection
        skip: !initialized, // Do not run query until initialization is triggered.
    });

    const {
        fallbackData,
        loading: loadingFallback,
        error: fallbackError,
    } = useQueryFallbackResponse({ query, locale, initialized, queryError: queryError });

    const initialize = () => {
        if (!initialized) {
            setInitialized(true);
        }
    };

    const resourceTypes = useMemo(() => {
        const data = fallbackData ?? queryData;
        if (!data) {
            return [];
        }

        const resourceTypes = transformResourceTypeResponse(data);

        const sortTreeByName = (items: ResourceType[]): ResourceType[] =>
            items
                .slice()
                .sort((a, b) => a.name.localeCompare(b.name, locale))
                .map((item) => ({
                    ...item,
                    children: item.children ? sortTreeByName(item.children) : undefined,
                }));

        return sortTreeByName(resourceTypes);
    }, [queryData, fallbackData, locale]);

    useEffect(() => {
        const resourceTypeById: ResourceTypeById = {};
        const resourceTypeByKey: ResourceTypeByKey = {};

        resourceTypes.forEach((type) => {
            resourceTypeById[type.id] = type;
            resourceTypeByKey[type.key] = type;

            type.children?.forEach((child) => {
                resourceTypeById[child.id] = child;
                resourceTypeByKey[`${type.key}/${child.key}`] = child;
            });
        });

        setResourceTypeById(resourceTypeById);
        setResourceTypeByKey(resourceTypeByKey);
    }, [resourceTypes]);

    const loading = loadingQuery || loadingFallback;
    // if we have fallback data, we don't consider it an error
    const error = fallbackError || (queryError && !fallbackData ? queryError : null);

    return (
        <ResourceTypesContext.Provider
            value={{
                resourceTypes,
                resourceTypeById,
                resourceTypeByKey,
                loading,
                error,
                initialize,
            }}
        >
            {children}
        </ResourceTypesContext.Provider>
    );
};

// Unsafe hook: does not trigger initialization.
function useResourceTypesUnsafe() {
    const context = useContext(ResourceTypesContext);
    if (!context) {
        throw new Error('useResourceTypes must be used within a ResourceTypesProvider');
    }
    return context;
}

// Safe hook: triggers lazy initialization when a consumer uses it.
function useResourceTypes() {
    const context = useResourceTypesUnsafe();
    useEffect(() => {
        context.initialize();
    }, [context]);
    return context;
}

export { ResourceTypesProvider, useResourceTypes, useResourceTypesUnsafe };
export type { ResourceTypesContextValue, ResourceTypeById, ResourceTypeByKey };
